/**
 * /admin/portal-codes — operator-side portal_codes management
 * (OCP-159). Mint codes that customers redeem on
 * account.orboto.io/redeem to activate a subscription or top-up
 * credits on their portal account.
 *
 * Different from /admin/codes which mints tenant-provisioning codes
 * for the public /v1/redeem flow (creates SaaS subdomains).
 */
import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';

import { api, ApiError } from '../lib/api';
import { fmtDate } from '../lib/format-time';

interface PortalCode {
  id: string;
  code: string;
  target_product_id: string;
  subscription_duration_days: number;
  max_uses: number;
  uses_count: number;
  expires_at: string | null;
  created_by: string;
  note: string | null;
  created_at: string;
}

interface Product {
  id: string;
  display_name: string;
  product_family: string;
  features: Record<string, unknown>;
}

export function AdminPortalCodesPage() {
  const [items, setItems] = useState<PortalCode[] | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [codes, prods] = await Promise.all([
        api<{ items: PortalCode[] }>('/v1/admin/portal-codes'),
        api<{ items: Product[] }>('/v1/admin/products'),
      ]);
      setItems(codes.items);
      setProducts(prods.items);
    } catch (err) {
      setError(err instanceof ApiError ? `HTTP ${err.status}` : 'Load failed');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function revoke(id: string) {
    if (!confirm('Revoke this code? Already-issued redemptions stay valid.')) return;
    try {
      await api(`/v1/admin/portal-codes/${id}`, { method: 'DELETE' });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? `Revoke failed (HTTP ${err.status})` : 'Revoke failed');
    }
  }

  return (
    <div>
      <header className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Portal codes</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Redeemable codes for activating subscriptions or top-up credits on
            customer accounts. Customers redeem at
            <code className="ml-1">account.orboto.io/redeem</code>.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
        >
          Mint code
        </button>
      </header>

      {error && (
        <div className="mb-4 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {!items && <p className="text-sm text-gray-500">Loading…</p>}

      {items && items.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-gray-500">
          No codes minted yet.
        </div>
      )}

      {items && items.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">Code</th>
                <th className="px-3 py-2">Target</th>
                <th className="px-3 py-2 text-right">Uses</th>
                <th className="px-3 py-2">Duration</th>
                <th className="px-3 py-2">Expires</th>
                <th className="px-3 py-2">Created by</th>
                <th className="px-3 py-2">Note</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {items.map((c) => {
                const expired = c.expires_at && new Date(c.expires_at) < new Date();
                const exhausted = c.uses_count >= c.max_uses;
                const inactive = expired || exhausted;
                return (
                  <tr key={c.id} className={inactive ? 'opacity-50' : ''}>
                    <td className="px-3 py-2 font-mono text-xs">{c.code}</td>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                      <code className="text-xs">{c.target_product_id}</code>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {c.uses_count} / {c.max_uses}
                    </td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                      {c.subscription_duration_days}d
                    </td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                      {c.expires_at
                        ? fmtDate(c.expires_at)
                        : '—'}
                    </td>
                    <td className="px-3 py-2 text-gray-500">{c.created_by}</td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{c.note ?? '—'}</td>
                    <td className="px-3 py-2 text-right">
                      {!inactive && (
                        <button
                          type="button"
                          onClick={() => void revoke(c.id)}
                          className="text-xs text-red-700 dark:text-red-300 hover:underline"
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <MintModal
          products={products}
          onCancel={() => setCreating(false)}
          onMinted={async () => {
            setCreating(false);
            await load();
          }}
        />
      )}
    </div>
  );
}

function MintModal({
  products,
  onCancel,
  onMinted,
}: {
  products: Product[];
  onCancel: () => void;
  onMinted: () => Promise<void>;
}) {
  const [targetProductId, setTargetProductId] = useState(products[0]?.id ?? '');
  const [duration, setDuration] = useState('30');
  const [maxUses, setMaxUses] = useState('1');
  const [expiresAt, setExpiresAt] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mintedCode, setMintedCode] = useState<string | null>(null);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await api<{ code: string }>('/v1/admin/portal-codes', {
        method: 'POST',
        body: {
          target_product_id: targetProductId,
          subscription_duration_days: Number(duration) || 30,
          max_uses: Number(maxUses) || 1,
          expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
          note: note.trim() || undefined,
        },
      });
      setMintedCode(r.code);
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | null;
        setError(body?.error ?? `HTTP ${err.status}`);
      } else {
        setError('Mint failed.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4"
      onClick={() => (mintedCode ? void onMinted() : onCancel())}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-xl"
      >
        {mintedCode ? (
          <div>
            <h2 className="mb-3 text-base font-semibold">Code minted</h2>
            <p className="mb-3 text-sm text-gray-700 dark:text-gray-300">
              Copy this code now — share it with the customer.
            </p>
            <code className="block break-all rounded border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-2 text-sm">
              {mintedCode}
            </code>
            <button
              type="button"
              onClick={() => void onMinted()}
              className="mt-4 w-full rounded bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-3 text-sm">
            <h2 className="text-base font-semibold">Mint portal code</h2>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-gray-600 dark:text-gray-400">Target product</span>
              <select
                value={targetProductId}
                onChange={(e) => setTargetProductId(e.target.value)}
                required
                className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5"
              >
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.display_name} ({p.id})
                  </option>
                ))}
              </select>
            </label>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  Duration (days, subs only)
                </span>
                <input
                  type="number"
                  min={1}
                  max={36500}
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 tabular-nums"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-600 dark:text-gray-400">Max uses</span>
                <input
                  type="number"
                  min={1}
                  value={maxUses}
                  onChange={(e) => setMaxUses(e.target.value)}
                  className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 tabular-nums"
                />
              </label>
            </div>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-gray-600 dark:text-gray-400">Expires at (optional)</span>
              <input
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-gray-600 dark:text-gray-400">Note (internal)</span>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. beta-wave-1"
                className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5"
              />
            </label>

            {error && (
              <div className="rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-xs text-red-700 dark:text-red-300">
                {error === 'code_already_exists' ? 'A code with that value already exists.' : error === 'product_not_found' ? 'That product no longer exists in the catalog.' : error}
              </div>
            )}

            <div className="mt-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={onCancel}
                className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/60"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:bg-gray-400"
              >
                {submitting ? '…' : 'Mint'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
